/**
 * AuthLayout: the shell of the signed-out pages (log in, sign up, forgot and reset password, verify email).
 * One logo, one card width, one heading style, so the pages line up when you move between them.
 *
 *   <AuthLayout title="Welcome back" description="Log in to continue to Spidra" pageTitle="Log in">...</AuthLayout>
 *
 * Also exports OAuthButtons (Google and GitHub, through the configured API base over https) and AuthNotice.
 */
import { ReactNode } from "react";
import Head from "next/head";
import Image from "next/image";
import { Icon } from "@iconify/react";
import api from "@/lib/axios";

export default function AuthLayout({
  title,
  description,
  pageTitle,
  children,
  footer,
}: {
  title: ReactNode;
  description?: ReactNode;
  pageTitle: string;
  children: ReactNode;
  footer?: ReactNode;
}) {
  return (
    <>
      <Head>
        <title>{`${pageTitle} | Spidra`}</title>
      </Head>
      <main className="flex min-h-dvh items-start justify-center bg-canvas px-4 py-12 sm:items-center">
        <div className="w-full max-w-md">
          <div className="mb-6 flex justify-center">
            <Image src="/images/logo-icon.png" alt="Spidra" width={48} height={48} priority />
          </div>
          <div className="rounded-card border border-line bg-surface p-6 shadow-card sm:p-8">
            <div className="mb-6 text-center">
              <h1 className="text-2xl font-semibold text-fg">{title}</h1>
              {description && <p className="mt-1 text-sm text-fg-muted">{description}</p>}
            </div>
            {children}
          </div>
          {footer && <div className="mt-6 text-center text-sm text-fg-muted">{footer}</div>}
        </div>
      </main>
    </>
  );
}

/** An inline message at the top of an auth form. */
export function AuthNotice({ tone, children }: { tone: "error" | "success" | "info"; children: ReactNode }) {
  const cls =
    tone === "error"
      ? "border-danger/30 bg-danger-subtle text-danger-text"
      : tone === "success"
        ? "border-success/30 bg-success-subtle text-success-text"
        : "border-line bg-surface-muted text-fg-secondary";
  return (
    <div role={tone === "error" ? "alert" : "status"} className={`mb-4 flex items-start gap-2 rounded-control border px-3 py-2.5 text-13 ${cls}`}>
      <Icon
        icon={tone === "error" ? "mdi:alert-circle-outline" : tone === "success" ? "mdi:check-circle-outline" : "mdi:information-outline"}
        className="mt-px shrink-0 text-base"
        aria-hidden="true"
      />
      <div className="min-w-0">{children}</div>
    </div>
  );
}

/**
 * Google and GitHub sign-in. Links go through the configured API base (https in production), not a hardcoded
 * http host. An MCP login flow token is passed along so the backend can resume the flow when it supports it.
 */
export function OAuthButtons({ mcpFlow }: { mcpFlow?: string | null }) {
  const base = (api.defaults.baseURL || "https://api.spidra.io/api").replace(/\/$/, "");
  const href = (provider: "google" | "github") => `${base}/auth/${provider}${mcpFlow ? `?mcp_flow=${encodeURIComponent(mcpFlow)}` : ""}`;
  const cls =
    "flex h-11 flex-1 items-center justify-center gap-2 rounded-control border border-line-strong bg-surface text-sm font-medium text-fg transition-colors hover:bg-surface-muted focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-primary";
  return (
    <>
      <div className="relative my-6">
        <div className="absolute inset-0 flex items-center" aria-hidden="true">
          <div className="w-full border-t border-line" />
        </div>
        <p className="relative flex justify-center text-13 text-fg-muted">
          <span className="bg-surface px-2">Or continue with</span>
        </p>
      </div>
      <div className="flex gap-3">
        <a href={href("google")} className={cls}>
          <Icon icon="flat-color-icons:google" className="text-xl" aria-hidden="true" />
          Google
        </a>
        <a href={href("github")} className={cls}>
          <Icon icon="mdi:github" className="text-xl text-fg" aria-hidden="true" />
          GitHub
        </a>
      </div>
    </>
  );
}

export const AuthRedirecting = () => (
  <div role="status" className="flex h-dvh items-center justify-center bg-canvas">
    <p className="text-sm text-fg-muted">Redirecting</p>
  </div>
);
